import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table,
  Input,
  Checkbox,
  Tag,
  Spin,
  message,
  Button
} from 'antd';
import { SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { adminUnitApi } from '@/services/api';

const { Search } = Input;

type UnitRow = {
  id: string | number;
  unitType?: string;   // API 목록에서 사용
  title?: string;       // API 목록에서 사용
  code?: string;
  orderIndex?: number;
  createdAt?: string;   // API 목록에서 사용
  unitCategory?: string; // 한글 카테고리(있으면 우선 표시)
  // 구버전/다른 페이지에서 전달하는 필드들 호환
  type?: string;
  name?: string;
  registrant?: string;
  registrationDate?: string;
};

interface UnitTableProps {
  onUnitSelect?: (selectedUnits: string[]) => void;
  selectedUnits?: string[];
  showCheckbox?: boolean;
  showSearch?: boolean;
  maxHeight?: string;
  size?: 'small' | 'middle';
  pagination?: false | TablePaginationConfig;
  onRowClick?: (record: any) => void;
  dataSource?: UnitRow[];
  rowClassName?: string;
  showDeleteButton?: boolean;
  onDelete?: (unitId: string) => void;
}

const UnitTable: React.FC<UnitTableProps> = ({
  onUnitSelect,
  selectedUnits = [],
  showCheckbox = false,
  showSearch = true,
  maxHeight = 'auto',
  size = 'middle',
  pagination = {
    pageSize: 10,
    showTotal: (total, range) => `총 ${total} 개`,
  },
  onRowClick,
  dataSource,
  rowClassName,
  showDeleteButton = false,
  onDelete
}) => {
  const [unitList, setUnitList] = useState<UnitRow[]>([]);
  // const [total, setTotal] = useState(0); // legacy
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const defaultPageSize = (typeof pagination === 'object' && pagination?.pageSize ? Number(pagination.pageSize) : 10);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    if (!dataSource) {
      loadUnitList();
    }
  }, [dataSource]);

  const loadUnitList = async () => {
    setLoading(true);
    try {
      // 전체 목록을 한 번에 로드하여 클라이언트에서 검색/페이지네이션 수행
      const res = await adminUnitApi.list({ q: '', page: 0, size: 1000 });
      const data = res?.data || res; // ApiResponse 또는 직접 데이터 보호
      const content = data?.content || data?.data?.content || [];
      setUnitList(content);
    } catch (error) {
      message.error('유닛 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setPage(1);
    setSearchText(value);
  };

  const handleUnitSelection = (unitId: string | number, checked: boolean) => {
    if (!onUnitSelect) return;

    let newSelectedUnits: string[];
    if (checked) {
      newSelectedUnits = [...selectedUnits, String(unitId)];
    } else {
      newSelectedUnits = selectedUnits.filter(id => id !== String(unitId));
    }
    onUnitSelect(newSelectedUnits);
  };

  // const handleSelectAll = (checked: boolean) => {
  //   if (!onUnitSelect) return;
  //   if (checked) {
  //     const list = (dataSource as UnitRow[] | undefined) || unitList;
  //     onUnitSelect(list.map((unit: UnitRow) => String(unit.id)));
  //   } else {
  //     onUnitSelect([]);
  //   }
  // };

  // 유형 한글 라벨 + 색상 태그
  const toUiLabel = useMemo(() => (val?: string): string => {
    switch (val) {
      case 'PHONICS_DAY': return '파닉스데이';
      case 'STARTER_PHONICS': return '스타터_파닉스';
      case 'STARTER_NUANCE': return '뉘앙스';
      case 'STARTER_STRUCTURE': return '스트럭처';
      case 'ESSAY': return '에세이';
      case 'TRANSLATION': return '통번역';
      case 'READING_BOOK': return '리딩북';
    }
    // 이미 한글 라벨인 경우 그대로
    return val || '';
  }, []);

  const typeColor = useMemo(() => (val?: string): string => {
    switch (val) {
      case '파닉스데이': return 'blue';
      case '스타터_파닉스': return 'geekblue';
      case '스타터_뉘앙스': return 'purple';
      case '스타터_스트럭처': return 'green';
      // 구버전 라벨 호환
      case '뉘앙스': return 'purple';
      case '스트럭처': return 'green';
      case '에세이': return 'orange';
      case '통번역': return 'gold';
      case '리딩북': return 'cyan';
      default: return 'default';
    }
  }, []);

  const formatYmd = useMemo(() => (val?: any): string => {
    if (!val) return '';
    const str = String(val);
    const m = str.match(/^\d{4}-\d{2}-\d{2}/);
    if (m) return m[0];
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${mm}-${dd}`;
    }
    return str;
  }, []);

  // 클라이언트 검색/필터링
  const baseList: UnitRow[] = (dataSource as UnitRow[] | undefined) || unitList;
  const filteredList: UnitRow[] = React.useMemo(() => {
    const q = (searchText || '').trim().toLowerCase();
    if (!q) return baseList;
    return baseList.filter((u) => {
      const title = String(u.title || u.name || '').toLowerCase();
      const code = String(u.code || '').toLowerCase();
      const type = String(u.unitCategory || u.unitType || u.type || '').toLowerCase();
      const registrant = String(u.registrant || '').toLowerCase();
      return title.includes(q) || code.includes(q) || type.includes(q) || registrant.includes(q);
    });
  }, [baseList, searchText]);

  const pagedList: UnitRow[] = React.useMemo(() => {
    if (pagination === false) return filteredList;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredList.slice(start, end);
  }, [filteredList, pagination, page, pageSize]);

  const filteredTotal = filteredList.length;

  const columns: ColumnsType<UnitRow> = [
    ...(showCheckbox ? [{
      title: '선택',
      dataIndex: 'id',
      key: 'selection',
      width: 60,
      align: 'center' as const,
      render: (unitId: any) => (
        <Checkbox
          checked={selectedUnits.includes(String(unitId))}
          onChange={(e) => handleUnitSelection(String(unitId), e.target.checked)}
        />
      )
    }] : []),
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => {
        const globalIndex = (page - 1) * pageSize + index;
        const grandTotal = filteredTotal;
        return grandTotal > 0 ? (grandTotal - globalIndex) : (globalIndex + 1);
      }
    },
    {
      title: '유형',
      dataIndex: 'unitType',
      key: 'unitType',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: UnitRow) => {
        const ui = toUiLabel(record.unitCategory || record.unitType || record.type || '');
        return <Tag color={typeColor(ui)}>{ui}</Tag>;
      }
    },
    {
      title: '유닛명',
      dataIndex: 'title',
      key: 'title',
      render: (_: any, record: UnitRow) => (
        <div style={{ fontWeight: 'bold', color: '#111827' }}>
          {record.title || record.name}
        </div>
      )
    },
    {
      title: '코드',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (code?: string) => code || '-'
    },
    {
      title: '등록자',
      dataIndex: 'registrant',
      key: 'registrant',
      width: 150,
      render: (value?: string) => value || '-'
    },
    {
      title: '등록일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (_: any, record: UnitRow) => (
        <div style={{ color: '#666' }}>
          {formatYmd(record.createdAt || record.registrationDate)}
        </div>
      )
    },
    ...(showDeleteButton ? [{
      title: '관리',
      key: 'actions',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: UnitRow) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(String(record.id));
          }}
        >
          삭제
        </Button>
      )
    }] : [])
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" tip="유닛 목록을 불러오는 중..." />
      </div>
    );
  }

  return (
    <div>
      {/* 검색 영역 */}
      {showSearch && (
        <div style={{ marginBottom: '16px' }}>
          <Search
            placeholder="유닛명 또는 등록자로 검색"
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: 400 }}
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      )}

      {/* 유닛 목록 테이블 */}
      <div style={{ maxHeight, overflow: 'auto' }}>
        <Table
          columns={columns}
          dataSource={pagedList}
          rowKey="id"
          pagination={pagination !== false ? {
            current: page,
            pageSize: pageSize,
            total: filteredTotal,
            showTotal: (t) => `총 ${t} 개`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); }
          } : false}
          size={size}
          rowClassName={rowClassName}
          onRow={onRowClick ? (record) => ({
            onClick: () => onRowClick(record),
            style: { cursor: 'pointer' }
          }) : undefined}
          locale={{
            emptyText: searchText ? '검색 결과가 없습니다.' : '등록된 유닛이 없습니다.'
          }}
        />
      </div>
    </div>
  );
};

export default UnitTable; 